/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { faBriefcase, faRectanglePro, faUser } from '@fortawesome/pro-solid-svg-icons';
import { useIsJobCoach } from '@App/components/AuthProvider/auth-provider.context';
import {
  Checkbox, Icon, Select, Search, Tooltip,
} from '@nazka/nazka.mapframe.components';
import { faSquare } from '@fortawesome/pro-light-svg-icons';
import { faClock, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  difference, polygon, featureCollection, bbox,
} from '@turf/turf';

import Chips from '@App/components/Chips/Chips';

import theme from '@css/theme.style.json';
import { useVacancyFilterOptions } from '@services/query.service';
import { getJobsFromVacancies } from '@App/pages/JobVacancies/job-vacancies.utils';

import {
  daysSinceReleaseLimitOptions, checkboxFilterLabels, languageLevels, 
} from './filter.constants';

import {
  updateDaysSinceReleaseLimit,
  updateJobDomain,
  updateJob,
  updateWorkSchedule,
  updateTimeArrangement,
  updateContractType,
  updateEducationLevel,
  updateDegree,
  updateLanguages,
  updateDriverLicense,
  updateRefundBusTrain,
  updateOrganizationCollectiveTransport,
  updateOrganizationCarpool,
  updateCarSharingSubscription,
  updateElectricCarChargingStation,
  updateCompanyCar,
  updateTelecommutingIsPossible,
  updateFlexibleHours,
  updateBicycleAllowance,
  updateCompanyOrLeaseBikes,
  updateChargingStationEBikes,
  updateChangingRoomAndOrShowers,
} from './filter.slice';
import { filterFilterOptions } from './filter.utils';

import './filter.styl';

const outlineCSSFix = v => ({
  '> button > span': {
    color: !v && theme.color.common.grey.light,
  },
});

function Filter() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isJobCoach = useIsJobCoach();

  const allVacancies = useSelector(state => state.map.allVacancies);

  const { map, scrollPositionRef } = useOutletContext();

  const { data: filterOptions, isLoading } = useVacancyFilterOptions();

  const filteredFilterOptions = useMemo(() => filterFilterOptions(allVacancies, filterOptions), [allVacancies, filterOptions]);

  const searchGeometry = useSelector(state => state.map.searchGeometry);
  const homeAddress = useSelector(state => state.map.homeAddress);
  const filter = useSelector(state => state.filter);
  const vacancies = useSelector(state => state.map.vacancies);

  const {
    electricCarChargingStation, jobDomain, job, workSchedule, timeArrangement, contractType, educationLevel,
    degree, languages, driverLicense, refundBusTrain, organizationCollectiveTransport, organizationCarpool,
    carSharingSubscription, companyCar, telecommutingIsPossible, flexibleHours, bicycleAllowance,
    companyOrLeaseBikes, chargingStationEBikes, changingRoomAndOrShowers, daysSinceReleaseLimit,
  } = filter;
  
  useEffect(() => {
    if (map && searchGeometry && map.getSource('search-geometry')) {
      const worldBounds = polygon([[[-180, -90], [180, -90], [180, 90], [-180, 90], [-180, -90]]]);
      const searchGeometryNegative = difference(featureCollection([worldBounds, searchGeometry]));
      map.getSource('search-geometry').setData(searchGeometryNegative);
      map.fitBounds(bbox(searchGeometry), {
        padding: 20, maxZoom: 15,
      });
    }

    return () => map && map.getSource('search-geometry').setData({
      type: 'FeatureCollection',
      features: [],
    });
  }, [map, searchGeometry]);

  useEffect(() => {
    scrollPositionRef.current = 0;
  }, [scrollPositionRef]);

  useEffect(() => {
    if (!homeAddress) navigate('/');
  }, [homeAddress, navigate]);

  const jobs = useMemo(() => {
    return getJobsFromVacancies(vacancies);
  }, [vacancies]);

  return (
    <div className={`filter${isLoading ? ' is-loading' : ''}`}>
      <Chips />
      <p className="filter__header">
        {`${jobs.length} vacature${jobs.length === 1 ? '' : 's'} `}
        {`in ${vacancies.length} bedrij${vacancies.length === 1 ? 'f' : 'ven'}`}
      </p>
      <div className="filter__body">
        <div className="filter__body__group">
          <div className="filter__body__group__options">
            <label className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Hoelang staat de vacature online?
              </span>
              <Select
                clear
                onInputChange={v => dispatch(updateDaysSinceReleaseLimit(v?.id || null))}
                options={daysSinceReleaseLimitOptions}
                value={daysSinceReleaseLimit}
                css={() => outlineCSSFix(daysSinceReleaseLimit)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer dagen sinds publicatie"
              />
            </label>
          </div>
        </div>
        <div className="filter__body__group">
          <h3 className="filter__body__group__title">
            <Icon
              className="filter__body__group__title__icon"
              icon={faBriefcase}
            />
            <span>
              Over de job
            </span>
          </h3>
          <div className="filter__body__group__options">
            <div className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Wat voor werk zoek je?
              </span>
              <Select
                clear
                isMulti
                onInputChange={v => dispatch(updateJobDomain(v || []))}
                options={filteredFilterOptions.jobdomeinen}
                value={jobDomain}
                css={() => outlineCSSFix(jobDomain && jobDomain.length)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer een jobdomein"
              />
            </div>
            <div className="filter__body__group__options__inputs__label">
              <Select
                isMulti
                clear
                onInputChange={v => dispatch(updateJob(v || []))}
                options={filteredFilterOptions.sjablonen}
                value={job}
                css={() => outlineCSSFix(job && job.length)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer een beroep"
              />
            </div>
          </div>
          <div className="filter__body__group__options">
            <label className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Welk type werkrooster zoek je?
                <Tooltip
                  id="work-schedule-tooltip"
                  // place="left"
                  tooltip={(
                    <div className="filter__body__group__options__inputs__label__text__tooltip__content">
                      <ul>
                        <li>Dagwerk: Dit zijn jobs waarbij je start tussen 7 en 9.30 uur ‘s morgens en stopt tussen 15.30 en 18 uur.</li>
                        <li>2-ploegenstelsel: Dit zijn jobs waarbij je werkt in 2 ploegen. De ene week werk je ‘s ochtends, de andere week ‘s avonds.</li>
                        <li>3-ploegenstelsel: Dit zijn jobs waarbij je werkt in 3 ploegen. Je werkt een week ‘s ochtends, ‘s avonds en ‘s nachts.</li>
                        <li>Nachtwerk: Dit zijn jobs waarbij je ‘s nachts werkt.</li>
                        <li>Weekendwerk: Je werkt op zaterdag en/of zondag.</li>
                        <li>Onderbroken dienst: Dit zijn jobs waarbij je werkdag minstens 4 uur onderbroken wordt. Bijvoorbeeld: je werkt van 6 tot 10 uur en van 16 tot 20 uur.</li>
                        <li>Volcontinu systeem: Dit zijn jobs waarbij je werkt in 5 ploegen: ‘s ochtends, overdag, ‘s avonds, ‘s nachts of in het weekend.</li>
                      </ul>
                    </div>
                  )}
                >
                  <Icon 
                    icon={faInfoCircle}
                    className="filter__body__group__options__inputs__label__text__tooltip"
                    rootElProps={{ 'data-tooltip-id': 'work-schedule-tooltip' }}
                  />
                </Tooltip>
              </span>
              <Select
                clear
                onInputChange={v => dispatch(updateWorkSchedule(v?.id || null))}
                options={filteredFilterOptions?.arbeidsregimes || []}
                value={workSchedule}
                css={() => outlineCSSFix(workSchedule)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Voltijds of deeltijds"
              />
            </label>
            <div className="filter__body__group__options__inputs__label">
              <Select
                clear
                isMulti
                onInputChange={v => dispatch(updateTimeArrangement(v || []))}
                options={filteredFilterOptions.arbeidsstelsels}
                value={timeArrangement}
                css={() => outlineCSSFix(timeArrangement && timeArrangement.length)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer een werkrooster"
              />
            </div>
          </div>
          <div className="filter__body__group__options">
            <label className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Welk type contract zoek je?
              </span>
              <Select
                clear
                onInputChange={v => dispatch(updateContractType(v?.id || null))}
                options={filteredFilterOptions.contract}
                value={contractType}
                css={() => outlineCSSFix(contractType)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Bepaald of onbepaald"
              />
            </label>
          </div>
        </div>
        <div className="filter__body__group">
          <h3 className="filter__body__group__title">
            <Icon
              className="filter__body__group__title__icon"
              icon={faUser}
            />
            <span>
              Jouw profiel
            </span>
          </h3>
          <div className="filter__body__group__options">
            <label className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Wat is je diploma?
                <Tooltip
                  id="diploma-tooltip"
                  // place="left"
                  tooltip={(
                    <div className="filter__body__group__options__inputs__label__text__tooltip__content">
                      <p>
                        {'Je geeft aan wat het hoogste diploma is dat je behaald hebt. '
                        + 'Indien er in een job specifieke diploma vereisten vermeld worden, moeten deze precies overeenkomen met '
                        + 'jouw diploma om de job in de zoekresultaten te laten verschijnen. Met een diploma lager onderwijs krijg '
                        + 'je dus geen jobs te zien waarvoor een bachelor diploma vereist is.'}
                      </p>
                      <p>
                        {'Maar let op: ook met masterdiploma krijg je geen jobs te zien waarvoor enkel een bachelor vereist is. '
                        + 'Als er in de job geen diploma vereisten gedefinieerd staan, krijg je deze job sowieso te zien.'}
                      </p>
                    </div>
                  )}
                >
                  <Icon 
                    icon={faInfoCircle}
                    className="filter__body__group__options__inputs__label__text__tooltip"
                    rootElProps={{ 'data-tooltip-id': 'diploma-tooltip' }}
                  />
                </Tooltip>
              </span>
              <Select
                clear
                onInputChange={v => dispatch(updateEducationLevel(v?.id || null))}
                options={filteredFilterOptions.diplomaniveau}
                value={educationLevel}
                css={() => outlineCSSFix(educationLevel)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer een onderwijsniveau"
              />
            </label>
            <label className="filter__body__group__options__inputs__label">
              <Search
                clear
                onSelect={v => dispatch(updateDegree(v?.id || null))}
                options={filteredFilterOptions.diplomas}
                value={degree}
                css={() => outlineCSSFix(degree)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Zoek op diploma"
              />
            </label>
          </div>
          <div className="filter__body__group__options">
            <div className="filter__body__group__options__inputs__label">
              <span className="filter__body__group__options__inputs__label__text">
                Welke talen ken je?
                {isJobCoach && (<Icon className="filter__body__group__options__inputs__label__text__pro" icon={faRectanglePro} />)}
                <Tooltip
                  id="language-tooltip"
                  // place="left"
                  tooltip={(
                    <div className="filter__body__group__options__inputs__label__text__tooltip__content">
                      <p>
                        Jobs met meerdere gevraagde talen krijg je ook te zien als je slechts 1 van de gevraagde talen spreekt.
                      </p>
                    </div>
                  )}
                >
                  <Icon 
                    icon={faInfoCircle}
                    className="filter__body__group__options__inputs__label__text__tooltip"
                    rootElProps={{ 'data-tooltip-id': 'language-tooltip' }}
                  />
                </Tooltip>
              </span>
              <Select
                clear
                isMulti
                onInputChange={(v) => {
                  dispatch(updateLanguages(v || []));
                }}
                options={filteredFilterOptions.talen}
                value={languages}
                css={() => outlineCSSFix(languages && languages.length)}
                className="filter__body__group__options__inputs__label__input"
                placeholder="Selecteer een taal"
              />
              {isJobCoach && (
                <div className="filter__body__group__options__language-pro">
                  {languages.map((language) => {
                    const { label } = language;
                    const level = languages.find(l => l.id === language?.id)?.data?.level || null;
                    const value = languageLevels.find(ll => ll?.id === level) || null;

                    return (
                      <div
                        className="filter__body__group__options__language-pro__item"
                        key={language?.id}
                      >
                        <div className="filter__body__group__options__language-pro__item__label">
                          <span className="filter__body__group__options__language-pro__item__label__text">
                            {label}
                          </span>
                          <Select
                            onInputChange={(v) => {
                              const languagesArray = languages.map((l) => {
                                if (l.id === language?.id) {
                                  return { ...l, data: { level: v?.id } };
                                }
                                
                                return l;
                              });
                              dispatch(updateLanguages(languagesArray || []));
                            }}
                            options={languageLevels}
                            value={value}
                            css={() => outlineCSSFix(value)}
                            className="filter__body__group__options__language-pro__item__label__input"
                            placeholder="Selecteer een taalniveau"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {isJobCoach && (
              <div className="filter__body__group__options__inputs__label">
                <span className="filter__body__group__options__inputs__label__text">
                  Welk rijbewijs heb je?
                  <Icon className="filter__body__group__options__inputs__label__text__pro" icon={faRectanglePro} />
                </span>
                <Select
                  clear
                  isMulti
                  onInputChange={v => dispatch(updateDriverLicense(v || []))}
                  options={filteredFilterOptions.rijbewijzen}
                  value={driverLicense}
                  css={() => outlineCSSFix(driverLicense && driverLicense.length)}
                  className="filter__body__group__options__inputs__label__input"
                  placeholder="Selecteer een rijbewijs"
                />
              </div>
            )}
          </div>
        </div>
        <div className="filter__body__group filter__body__group--mobiliteitsaanbod">
          <h3 className="filter__body__group__title">
            <Icon
              className="filter__body__group__title__icon"
              icon="/img/icons/mobiliteitsaanbod.svg"
            />
            <span>
              Mobiliteitsaanbod van de werkgever
            </span>
          </h3>
          <div className="filter__body__group__options filter__body__group__options--checkboxes">
            <div className="filter__body__group__options__title">
              <Icon 
                className="filter__body__group__options__title__icon"
                icon="/img/icons/transport_types/bike.svg"
                size={{ width: '100%', height: '35px' }}
              />
            </div>
            <div className="filter__body__group__options__inputs">
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.bicycleAllowance}
                  checked={bicycleAllowance}
                  onInputChange={v => dispatch(updateBicycleAllowance(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.companyOrLeaseBikes}
                  checked={companyOrLeaseBikes}
                  onInputChange={v => dispatch(updateCompanyOrLeaseBikes(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.chargingStationEBikes}
                  checked={chargingStationEBikes}
                  onInputChange={v => dispatch(updateChargingStationEBikes(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.changingRoomAndOrShowers}
                  checked={changingRoomAndOrShowers}
                  onInputChange={v => dispatch(updateChangingRoomAndOrShowers(v.target.checked))}
                  icon={faSquare}
                />
              </label>
            </div>
          </div>
          <div className="filter__body__group__options filter__body__group__options--checkboxes">
            <div className="filter__body__group__options__title">
              <Icon 
                className="filter__body__group__options__title__icon"
                icon="/img/icons/transport_types/ov.svg"
                size={{ width: '70%', height: '35px' }}
              />
            </div>
            <div className="filter__body__group__options__inputs">
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.refundBusTrain}
                  checked={refundBusTrain}
                  onInputChange={v => dispatch(updateRefundBusTrain(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.organizationCollectiveTransport}
                  checked={organizationCollectiveTransport}
                  onInputChange={v => dispatch(updateOrganizationCollectiveTransport(v.target.checked))}
                  icon={faSquare}
                />
              </label>
            </div>
          </div>
          <div className="filter__body__group__options filter__body__group__options--checkboxes">
            <div className="filter__body__group__options__title">
              <Icon 
                className="filter__body__group__options__title__icon"
                icon={faClock}
                size={{ width: '80%', height: '35px' }}
              />
            </div>
            <div className="filter__body__group__options__inputs">
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.telecommutingIsPossible}
                  checked={telecommutingIsPossible}
                  onInputChange={v => dispatch(updateTelecommutingIsPossible(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.flexibleHours}
                  checked={flexibleHours}
                  onInputChange={v => dispatch(updateFlexibleHours(v.target.checked))}
                  icon={faSquare}
                />
              </label>
            </div>
          </div>
          <div className="filter__body__group__options filter__body__group__options--checkboxes">
            <div className="filter__body__group__options__title">
              <Icon 
                className="filter__body__group__options__title__icon"
                icon="/img/icons/transport_types/car.svg"
                size={{ width: '100%', height: '35px' }}
              />
            </div>
            <div className="filter__body__group__options__inputs">
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.organizationCarpool}
                  checked={organizationCarpool}
                  onInputChange={v => dispatch(updateOrganizationCarpool(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              <label className="filter__body__group__options__inputs__label">
                <Checkbox
                  className="filter__body__group__options__inputs__label__checkbox"
                  text={checkboxFilterLabels.carSharingSubscription}
                  checked={carSharingSubscription}
                  onInputChange={v => dispatch(updateCarSharingSubscription(v.target.checked))}
                  icon={faSquare}
                />
              </label>
              {isJobCoach && (
                <label className="filter__body__group__options__inputs__label">
                  <Checkbox
                    className="filter__body__group__options__inputs__label__checkbox"
                    text={(
                      <div className="filter__body__group__options__inputs__label__checkbox__label">
                        {checkboxFilterLabels.electricCarChargingStation}
                        <Icon 
                          className="filter__body__group__options__inputs__label__checkbox__label__pro"
                          icon={faRectanglePro}
                        />
                      </div>
                    )}
                    checked={electricCarChargingStation}
                    onInputChange={v => dispatch(updateElectricCarChargingStation(v.target.checked))}
                    icon={faSquare}
                  />
                </label>
              )}
              {isJobCoach && (
                <label className="filter__body__group__options__inputs__label">
                  <Checkbox
                    className="filter__body__group__options__inputs__label__checkbox"
                    text={(
                      <div className="filter__body__group__options__inputs__label__checkbox__label">
                        {checkboxFilterLabels.companyCar}
                        <Icon 
                          className="filter__body__group__options__inputs__label__checkbox__label__pro"
                          icon={faRectanglePro}
                        />
                      </div>
                    )}
                    checked={companyCar}
                    onInputChange={v => dispatch(updateCompanyCar(v.target.checked))}
                    icon={faSquare}
                  />
                </label>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Filter;
